/**
 * ConfirmModal Component
 * 확인/취소 선택을 위한 공통 모달 컴포넌트
 */
import React from 'react';

interface ConfirmModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

// 스타일 상수
const styles = {
    overlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(2px)'
    },
    modal: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '400px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
        animation: 'confirmModalSlideIn 0.2s ease-out'
    },
    header: {
        padding: '20px 24px 16px',
        borderBottom: '1px solid #e5e7eb'
    },
    title: {
        margin: 0,
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    warningIcon: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#fef3c7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#d97706',
        fontSize: '0.875rem'
    },
    body: {
        padding: '16px 24px 24px',
        color: '#4b5563',
        fontSize: '0.9375rem',
        lineHeight: 1.6
    },
    footer: {
        padding: '16px 24px',
        backgroundColor: '#f9fafb',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px'
    },
    button: {
        padding: '10px 20px',
        borderRadius: '8px',
        fontWeight: 500,
        fontSize: '0.875rem',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    cancelButton: {
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        color: '#374151'
    },
    confirmButton: {
        backgroundColor: 'var(--vroong-green, #00C4B4)',
        border: '1px solid var(--vroong-green, #00C4B4)',
        color: '#ffffff'
    }
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    title,
    message,
    confirmText = '확인',
    cancelText = '취소',
    onConfirm,
    onCancel
}) => {
    if (!isOpen) return null;

    /** 배경 클릭 시 닫기 */
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onCancel();
        }
    };

    return (
        <div style={styles.overlay} onClick={handleBackdropClick}>
            <style>
                {`
                    @keyframes confirmModalSlideIn {
                        from {
                            opacity: 0;
                            transform: translateY(-10px) scale(0.98);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }
                    .confirm-modal-cancel-btn:hover {
                        background-color: #f3f4f6 !important;
                        border-color: #9ca3af !important;
                    }
                    .confirm-modal-confirm-btn:hover {
                        opacity: 0.9;
                    }
                `}
            </style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div style={styles.header}>
                    <h3 style={styles.title}>
                        <span style={styles.warningIcon}>
                            <i className="fas fa-exclamation"></i>
                        </span>
                        {title}
                    </h3>
                </div>

                {/* 본문 */}
                <div style={styles.body}>
                    {message}
                </div>

                {/* 푸터 */}
                <div style={styles.footer}>
                    <button
                        style={{ ...styles.button, ...styles.cancelButton }}
                        className="confirm-modal-cancel-btn"
                        onClick={onCancel}
                    >
                        {cancelText}
                    </button>
                    <button
                        style={{ ...styles.button, ...styles.confirmButton }}
                        className="confirm-modal-confirm-btn"
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
